import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { usePartnerConfig, isArticleEligibleForPartner, isArticleEligibleForPartnerFeature } from '../../lib/partnerConfig';
import { trackPartnerEvent } from '../../lib/analytics';

const seenPlacements = new Set();

const useImpression = (ref, cfg, placementId) => {
  useEffect(() => {
    if (!cfg || !ref.current || seenPlacements.has(placementId)) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !seenPlacements.has(placementId)) {
        seenPlacements.add(placementId);
        trackPartnerEvent('partner_feature_view', {
          partner_id: cfg.partner_id,
          placement_id: placementId,
          page: window.location.pathname,
          experiment_version: cfg.experiment_version || 'v1',
        });
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [cfg, placementId, ref]);
};

const trackClick = (cfg, placementId, cta) => {
  trackPartnerEvent('affiliate_click', {
    partner_id: cfg.partner_id,
    placement_id: placementId,
    page: window.location.pathname,
    cta,
    experiment_version: cfg.experiment_version || 'v1',
  });
};

export const BiminiPartnerFeature = ({ placementId, variant = 'full', hideGuideLink = false, primaryHref = null }) => {
  const cfg = usePartnerConfig('bimini');
  const ref = useRef(null);
  useImpression(ref, cfg, placementId);
  if (!cfg || cfg.enabled === false) return null;

  const copy = cfg.feature_copy || {};
  const compact = variant === 'compact';

  return (
    <aside
      ref={ref}
      className={`bg-zinc-900/80 border border-[#00B4D8]/25 ${compact ? 'p-5' : 'p-6 md:p-8'} my-10`}
      data-testid={`bimini-feature-${placementId}`}
      data-placement-id={placementId}
      data-partner-id="bimini"
      aria-label="Featured recovery partner"
    >
      <div className={`flex ${compact ? 'flex-col sm:flex-row items-start sm:items-center' : 'flex-col md:flex-row'} gap-5 md:gap-8`}>
        {cfg.image?.src && (
          <img
            src={cfg.image.src}
            alt={cfg.image.alt || 'Bimini NanoJet'}
            loading="lazy"
            className={compact ? 'w-24 h-24 object-cover flex-shrink-0' : 'w-full md:w-64 aspect-[4/3] object-cover flex-shrink-0'}
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[#00B4D8] text-[11px] uppercase tracking-[0.25em] font-bold mb-2" data-testid={`bimini-feature-eyebrow-${placementId}`}>
            {copy.eyebrow}
          </p>
          <h3 className={`text-white ${compact ? 'text-xl' : 'text-2xl md:text-3xl'} mb-2`} style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
            {copy.headline}
          </h3>
          <p className="text-zinc-400 text-sm leading-relaxed mb-3">{copy.body}</p>
          {cfg.discount?.active && cfg.discount?.label && (
            <p className="text-[#00B4D8] text-xs font-bold mb-3" data-testid={`bimini-feature-discount-${placementId}`}>
              {cfg.discount.label}
            </p>
          )}
          <p className="text-zinc-400 text-[11px] leading-relaxed mb-4" data-testid={`bimini-feature-disclosure-${placementId}`}>
            {cfg.disclosure}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {primaryHref && primaryHref.startsWith('/') ? (
              <Link
                to={primaryHref}
                onClick={() => trackClick(cfg, placementId, 'primary')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00B4D8] text-black text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
                data-partner-id="bimini"
                data-placement-id={placementId}
                data-testid={`bimini-feature-cta-${placementId}`}
              >
                {copy.cta_primary} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : (
            <a
              href={cfg.destination_url}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => trackClick(cfg, placementId, 'primary')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00B4D8] text-black text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
              data-partner-id="bimini"
              data-placement-id={placementId}
              data-testid={`bimini-feature-cta-${placementId}`}
            >
              {copy.cta_primary} <ExternalLink className="w-3.5 h-3.5" />
            </a>
            )}
            {cfg.guide_url && !hideGuideLink && (
              <Link
                to={cfg.guide_url}
                onClick={() => trackClick(cfg, placementId, 'secondary')}
                className="inline-flex items-center gap-1.5 text-[#00B4D8] text-xs font-bold uppercase tracking-wider hover:text-white transition-colors"
                data-testid={`bimini-feature-guide-${placementId}`}
              >
                {copy.cta_secondary} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

// Article placement: the featured card renders on ALL recovery-eligible
// articles (feature eligibility — split from popup eligibility). On the Bimini
// guide article itself the "Read our guide" self-link is hidden.
export const BiminiArticleFeature = ({ article }) => {
  const cfg = usePartnerConfig('bimini');
  if (!cfg || !isArticleEligibleForPartnerFeature(article, cfg)) return null;
  const hideGuideLink = cfg.guide_url === `/blogs/article/${article.slug}`;
  return <BiminiPartnerFeature placementId={`article-${article.slug}`} hideGuideLink={hideGuideLink} />;
};

// Locator therapy-hub placement: compact variant on recovery-relevant therapy hubs.
export const BiminiLocatorFeature = ({ therapy }) => {
  const cfg = usePartnerConfig('bimini');
  if (!cfg || cfg.enabled === false) return null;
  const list = cfg.eligibility?.locator_therapies || [];
  if (!list.includes(therapy)) return null;
  return <BiminiPartnerFeature variant="compact" placementId={`locator-${therapy}`} />;
};
